import React from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Card, CardContent, Typography, Box, Stack, Divider } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const RiskSummaryCards = ({ data, onCardClick }) => {
  if (!data) {
    return (
      <Grid2 container>
        <Grid2 xs={12}>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}
          >
            No risk summary data available
          </Typography>
        </Grid2>
      </Grid2>
    );
  }

  const riskLevels = [
    {
      level: 'critical',
      label: 'Critical Risk',
      Icon: ErrorIcon,
      muiColor: 'error.main',
      subtitle: 'Immediate Action',
      classification: 'HIGH_RISK_CREEP',
      minRiskScore: 8,
    },
    {
      level: 'high',
      label: 'High Risk',
      Icon: WarningAmberIcon,
      muiColor: 'warning.main',
      subtitle: 'This Week',
      minRiskScore: 6,
      maxRiskScore: 7,
    },
    {
      level: 'moderate',
      label: 'Moderate Risk',
      Icon: InfoIcon,
      muiColor: 'info.main',
      subtitle: 'This Month',
      classification: 'MODERATE_CREEP',
      minRiskScore: 4,
      maxRiskScore: 5,
    },
    {
      level: 'low',
      label: 'Low / Appropriate',
      Icon: CheckCircleIcon,
      muiColor: 'success.main',
      subtitle: 'Monitored',
      classification: 'APPROPRIATE_ACCESS',
      minRiskScore: null,
    },
  ];

  const getRiskCount = (level) => {
    if (!data.summary) return 0;
    const levelData = data.summary[level];
    return levelData ? levelData.count : 0;
  };

  const getAvgRiskScore = (level) => {
    if (!data.summary) return 0;
    const levelData = data.summary[level];
    return levelData ? parseFloat(levelData.avg_risk_score).toFixed(1) : '0.0';
  };

  const handleCardClick = (riskConfig) => {
    if (onCardClick) {
      const filterParams = {};
      if (riskConfig.classification) filterParams.classification = riskConfig.classification;
      if (riskConfig.minRiskScore) filterParams.minRiskScore = riskConfig.minRiskScore;
      if (riskConfig.maxRiskScore) filterParams.maxRiskScore = riskConfig.maxRiskScore;
      onCardClick(filterParams);
    }
  };

  return (
    <Grid2 container spacing={2}>
      {riskLevels.map((riskConfig) => {
        const count = getRiskCount(riskConfig.level);
        const avgScore = getAvgRiskScore(riskConfig.level);
        const { Icon } = riskConfig;

        return (
          <Grid2 key={riskConfig.level} xs={12} sm={6} lg={3}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: riskConfig.muiColor,
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => handleCardClick(riskConfig)}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        letterSpacing: '0.8px',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {riskConfig.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ color: riskConfig.muiColor, fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {count}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'background.subtle' }}>
                    <Icon sx={{ color: riskConfig.muiColor, fontSize: 22 }} />
                  </Box>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {riskConfig.subtitle}
                  </Typography>
                  {avgScore !== '0.0' && (
                    <Typography variant="caption" color="text.secondary">
                      Avg: <strong>{avgScore}</strong>
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid2>
        );
      })}
    </Grid2>
  );
};

export default RiskSummaryCards;
